// shortcutOverridesStore — ショートカットのリバインド差分を localStorage に永続化
// する infrastructure 層ユーティリティ (shortcut-settings.md §3、スライス2)。
//
// Design (UserPreferencesModal / uiScalePreference / AdminTokenStore と同流儀):
// - **差分のみ保存**: ユーザが既定から変更した action だけを `overrides` に持つ。
//   既定を触らなければ将来の CSP 整合キーマップ変更が自動で流れる
//   (= 「ショートカット安定性」competitive-landscape.md §5.4-bis と整合)。
//   → reset = override 削除 (= 既定へ戻す)。
// - **スキーマ**: `{ version: 1, overrides: { [actionId]: string[] } }`。
//   - `string[]` は実効キー集合 (例 `['p','b']` / `['Ctrl+m']` / `[]` = 未割当)。
//   - 空配列 `[]` は「未割当」を意味する有効な override (steal-with-warn で旧 action を
//     未割当化したときに残る)。既定へ戻したいときは entry ごと削除する。
// - **永続化 scope**: ユーザ端末ローカル, サーバ同期なし, protocol 変更なし
//   (= ショートカットは純クライアント表示差、modern-layout.md §9.4 大前提)。
// - **不正値 / storage 不可時の挙動**: parse 失敗 / SSR / Safari private browsing は
//   空 overrides 扱い (= 全 action が既定)。例外を throw しない。
//
// Tests: shortcutOverridesStore.test.ts (storage-injection style、uiScalePreference と
// 同じ Map ベース fake)。

/** localStorage キー: ショートカット override 差分。`oekaki:` namespace 踏襲。 */
export const SHORTCUT_OVERRIDES_KEY = 'oekaki:shortcut-overrides';

/** 永続化スキーマの version。将来 migration が要るときに分岐する。 */
export const SHORTCUT_OVERRIDES_VERSION = 1 as const;

/**
 * action id → 実効キー配列の差分マップ。**既定から変更した action だけ**を持つ。
 * 値 `[]` は「未割当」を表す有効な override (= 既定へ戻すのは entry 削除)。
 */
export type ShortcutOverrides = Readonly<Record<string, readonly string[]>>;

/** localStorage に書かれる JSON の形 (version + overrides)。 */
interface ShortcutOverridesEnvelope {
  readonly version: typeof SHORTCUT_OVERRIDES_VERSION;
  readonly overrides: ShortcutOverrides;
}

/**
 * Minimal Storage shape (uiScalePreference / AdminTokenStore と同流儀)。
 * `globalThis.localStorage` matches; tests pass a Map ベース fake。
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ShortcutOverridesStore {
  /** 全 override を読み出す。不在 / 不正 / storage 不可 → 空 `{}`。 */
  getAll(): ShortcutOverrides;
  /**
   * 1 action の override を設定する (= 既定と異なるキー集合を保存)。空配列 `[]` は
   * 「未割当」を表す有効な override として保存される。storage 不可なら no-op。
   */
  setOne(actionId: string, keys: readonly string[]): void;
  /** 1 action の override を削除する (= 既定へ戻す)。storage 不可なら no-op。 */
  resetOne(actionId: string): void;
  /** 全 override を削除する (= 全て既定へ戻す)。storage 不可なら no-op。 */
  resetAll(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`。
 * uiScalePreference / AdminTokenStore の probe を流用 (sentinel write + readback で
 * Safari private browsing / QuotaExceeded を弾く)。
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:shortcut-overrides__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

/**
 * JSON 文字列を `ShortcutOverrides` に narrow する。version 不一致 / 形不正は
 * 空 `{}` 扱い (= 安全側、例外を投げない)。値の各要素は string のみ残す
 * (= 異種混入を防ぐ defensive parse)。
 */
const parseOverrides = (raw: string | null): ShortcutOverrides => {
  if (raw === null) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const env = parsed as Partial<ShortcutOverridesEnvelope>;
    if (env.version !== SHORTCUT_OVERRIDES_VERSION) return {};
    const ov = env.overrides;
    if (typeof ov !== 'object' || ov === null) return {};
    const out: Record<string, readonly string[]> = {};
    for (const [id, keys] of Object.entries(ov)) {
      if (!Array.isArray(keys)) continue;
      out[id] = keys.filter((k): k is string => typeof k === 'string');
    }
    return out;
  } catch {
    return {};
  }
};

/** `ShortcutOverrides` を envelope に包んで JSON 文字列化する。 */
const serializeOverrides = (overrides: ShortcutOverrides): string =>
  JSON.stringify({
    version: SHORTCUT_OVERRIDES_VERSION,
    overrides,
  } satisfies ShortcutOverridesEnvelope);

export interface CreateShortcutOverridesStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getAll は常に空 `{}`、setter は黙って drop)。テストでは Map ベース fake。
   */
  storage?: StorageLike | null;
}

/**
 * `ShortcutOverridesStore` のインスタンスを構築。storage が unavailable な環境
 * (SSR / Safari private browsing 厳格モード) でも getAll は空を返すだけで、setter は
 * no-op (= 例外で UI が壊れない)。
 */
export const createShortcutOverridesStore = (
  opts: CreateShortcutOverridesStoreOptions = {},
): ShortcutOverridesStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  const readAll = (): ShortcutOverrides => {
    if (storage === null) return {};
    try {
      return parseOverrides(storage.getItem(SHORTCUT_OVERRIDES_KEY));
    } catch {
      return {};
    }
  };

  const writeAll = (overrides: ShortcutOverrides): void => {
    if (storage === null) return;
    try {
      if (Object.keys(overrides).length === 0) {
        // 空になったら entry 自体を消す (= localStorage を散らかさない)。
        storage.removeItem(SHORTCUT_OVERRIDES_KEY);
        return;
      }
      storage.setItem(SHORTCUT_OVERRIDES_KEY, serializeOverrides(overrides));
    } catch {
      // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる。
    }
  };

  return {
    getAll: readAll,
    setOne(actionId, keys) {
      const next = { ...readAll(), [actionId]: [...keys] };
      writeAll(next);
    },
    resetOne(actionId) {
      const current = readAll();
      if (!(actionId in current)) return;
      const next = { ...current };
      delete next[actionId];
      writeAll(next);
    },
    resetAll() {
      if (storage === null) return;
      try {
        storage.removeItem(SHORTCUT_OVERRIDES_KEY);
      } catch {
        // ignore
      }
    },
  };
};
